import { format } from 'date-fns';
import { X } from 'lucide-react';
import React from 'react';
import { Button } from '@/components/ui/button';
import { getStatusMapping } from '@/lib/utils';
import type { VorgangFilters, DateRange } from '@/types/filters';

interface FilterItemProps {
  filter: {
    key: string;
    value: unknown;
    displayText: string;
  };
  onRemove: (key: string) => void;
}

interface FilterSummaryProps {
  archiveFilters?: boolean;
  filters: VorgangFilters;
  hasActiveFilters: boolean;
  onRemoveFilter: (key: string) => void;
  onClearAllFilters: () => void;
}

/**
 * Helper function to get filter display text
 */
const getFilterDisplayText = (key: string, value: unknown): string => {
  switch (key) {
    case 'status': {
      if (!value || value === '') return '';
      const statusValues = (value as string).split(',').filter(Boolean);
      const statusLabel = (status: string) =>
        getStatusMapping(status, status === 'GELOESCHT').text;
      if (statusValues.length === 1) {
        return `Status: ${statusLabel(statusValues[0])}`;
      }
      return `Status: ${statusValues.map(statusLabel).join(', ')}`;
    }
    case 'createdAt': {
      const dateRange = value as DateRange;
      if (dateRange.from && dateRange.to) {
        return `Datum: ${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}`;
      } else if (dateRange.from) {
        return `Datum ab: ${format(dateRange.from, 'dd.MM.yyyy')}`;
      } else if (dateRange.to) {
        return `Datum bis: ${format(dateRange.to, 'dd.MM.yyyy')}`;
      }
      return '';
    }
    case 'currentGestartedAm': {
      const dateRange = value as DateRange;
      if (dateRange.from && dateRange.to) {
        return `Bearbeitungsdatum: ${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}`;
      } else if (dateRange.from) {
        return `Bearbeitungsdatum ab: ${format(dateRange.from, 'dd.MM.yyyy')}`;
      } else if (dateRange.to) {
        return `Bearbeitungsdatum bis: ${format(dateRange.to, 'dd.MM.yyyy')}`;
      }
      return '';
    }
    case 'auftragsnummer':
      return `Auftragsnummer: ${value}`;
    case 'vertragsnummer':
      return `Vertragsnummer: ${value}`;
    case 'kundennummer':
      return `Kundennummer: ${value}`;
    case 'correlationId':
      return `CorrelationId: ${value}`;
    case 'vorgangType':
      return `Vorgangs-Typ: ${value}`;
    case 'externerName':
      return `Externer Name: ${value}`;
    case 'externeId':
      return `Event-ID: ${value}`;
    case 'error':
      return `Fehler: ${value}`;
    case 'fehlertyp':
      return `Fehlertyp: ${value}`;
    case 'archivierungsstatus':
      return `Archivierung: ${value === 'ERFOLGREICH' ? 'Ja' : 'Nein'}`;
    case 'dokumentTyp':
      return `Dokumenttyp: ${value}`;
    case 'lockedBy':
      return `Gesperrt: ${value}`;
    case 'searchText':
      return `Suche: "${value}"`;
    case 'unterdrueckung':
      return `Unterdrückung: ${value}`;
    case 'currentStep':
      return `Aktueller Prozessschritt: ${value}`;
    case 'stale24h':
      return 'Status länger als 24h';
    default:
      return '';
  }
};

/**
 * Individual filter item component
 */
const FilterItem: React.FC<FilterItemProps> = ({ filter, onRemove }) => {
  return (
    <div className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm">
      <span>{filter.displayText}</span>
      <button
        onClick={() => onRemove(filter.key)}
        className="hover:bg-blue-200 rounded-full p-0.5"
        title={`${filter.displayText} entfernen`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

/**
 * Component that displays active filters with the ability to remove individual filters or clear all
 */
export const FilterSummary: React.FC<FilterSummaryProps> = ({
  filters,
  archiveFilters = false,
  hasActiveFilters,
  onRemoveFilter,
  onClearAllFilters,
}) => {
  if (!hasActiveFilters) return null;

  // Transform filters to active filters for display
  const activeFilters = Object.entries(filters)
    .filter(([key, value]) => {
      if (
        key === 'searchText' ||
        key === 'searchFields' ||
        key.endsWith('Operator')
      ) {
        return false;
      }
      if (key === 'createdAt' || key === 'currentGestartedAm') {
        const dateRange = value as DateRange;
        return dateRange.from || dateRange.to;
      }
      if (key === 'stale24h') {
        return value === true;
      }
      return value && value !== '';
    })
    .map(([key, value]) => {
      const displayText = getFilterDisplayText(key, value);
      if (
        !archiveFilters ||
        ![
          'auftragsnummer',
          'vertragsnummer',
          'externerName',
          'dokumentTyp',
        ].includes(key)
      ) {
        return { key, value, displayText };
      }
      const modeLabel =
        filters[(key + 'Operator') as keyof VorgangFilters] === 'equals'
          ? 'Ist gleich'
          : 'Enthält';
      return { key, value, displayText: `${displayText} (${modeLabel})` };
    });

  if (activeFilters.length === 0) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-blue-900">
            Aktive Filter ({activeFilters.length}):
          </span>
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter) => (
              <FilterItem
                key={filter.key}
                filter={filter}
                onRemove={onRemoveFilter}
              />
            ))}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onClearAllFilters}
          className="text-blue-700 border-blue-300 hover:bg-blue-100"
        >
          Alle Filter löschen
        </Button>
      </div>
    </div>
  );
};
